import React from 'react';
import styled, { useTheme } from 'styled-components';
import Skeleton from 'react-loading-skeleton';

import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useMatchMedia } from '@make-software/cspr-design';
import { AxisDomain } from 'recharts/types/util/types';
import { GraphComponentProps, YAxisRangeStartType } from '../chart.types';
import { GraphTooltip } from '../graph-tooltip';

const ChartContainer = styled.div<{ multipleGraphChart?: boolean }>(
  ({ theme, multipleGraphChart }) =>
    theme.withMedia({
      display: 'flex',
      width: '100%',
      padding: multipleGraphChart
        ? ['32px 0 ', '40px 0', '40px 0']
        : '40px 0 0 0',
      fontSize: '10px',
    })
);

const yAxisRangeDataType = {
  [YAxisRangeStartType.Zero]: [0, 'dataMax'],
  [YAxisRangeStartType.MinValue]: ['dataMin', 'dataMax'],
};

export const GraphComponent = ({
  graphData,
  loading,
  yAxisLabelText,
  yAxisValueKey,
  yAxisTickFormatter,
  yAxisTick,
  tooltipFormatter,
  xAxisTickFormatter,
  axisLine = false,
  multipleGraphChart,
  yAxisRangeStart = YAxisRangeStartType.Zero,
  description,
  title,
}: GraphComponentProps) => {
  const theme = useTheme();
  const responsiveSkeletonWidth = useMatchMedia([330, 664, 872, 1136], [theme]);

  const graphAxisData: Record<string, any> = graphData.reduce((acc, value) => {
    if (!value.data) {
      return acc;
    }
    value.data.forEach((dataItem) => {
      // derive values for y-axis
      if (!acc[dataItem[yAxisValueKey]]) {
        acc[dataItem[yAxisValueKey]] = { name: dataItem[yAxisValueKey] };
      }

      // derive values for x-axis
      acc[dataItem[yAxisValueKey]][value.graphKey] =
        dataItem[value.xAxisValueKey];
    });
    return acc;
  }, {});

  const data = Object.values(graphAxisData);

  const compareByHighestValueForKey = (a, b) => {
    const getArrayOfValuesByKeys = (obj) =>
      (obj.data ?? []).map((item) => item[obj.xAxisValueKey]);

    const arrayA = getArrayOfValuesByKeys(a);
    const arrayB = getArrayOfValuesByKeys(b);

    const highestValueA = Math.max(...arrayA);
    const highestValueB = Math.max(...arrayB);
    return highestValueB - highestValueA;
  };

  //sorting the data in descending order to render areas from the area with the highest value to the area with the lowest value to prevent cropping of parts of the graphs
  const graphDataDesc = graphData.sort(compareByHighestValueForKey);

  const graphDataLengthEmpty = graphData.length === 0;
  if (graphDataLengthEmpty) {
    return <ChartContainer>No data.</ChartContainer>;
  }

  const yAxisStartOptions = yAxisRangeDataType[yAxisRangeStart] as AxisDomain;

  return (
    <ChartContainer multipleGraphChart={multipleGraphChart}>
      <ResponsiveContainer width={'100%'} height={400}>
        {loading ? (
          <>
            <Skeleton width={responsiveSkeletonWidth} height={400} />
          </>
        ) : (
          <AreaChart
            data={data}
            width={500}
            height={400}
            title={title}
            desc={description}
          >
            <defs>
              {graphDataDesc.map((item) => (
                <linearGradient
                  id={item.graphKey}
                  key={item.graphKey}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={item.themeColor}
                    stopOpacity={0.16}
                  />
                  <stop
                    offset="95%"
                    stopColor={item.themeColor}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              vertical={false}
              stroke={theme.styleguideColors.borderPrimary}
            />
            <XAxis
              dataKey="name"
              tickFormatter={xAxisTickFormatter}
              stroke={theme.styleguideColors.contentSecondary}
              axisLine={axisLine}
              tickSize={0}
              tickMargin={10}
              minTickGap={69}
              interval="preserveEnd"
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              tick={yAxisTick}
              stroke={theme.styleguideColors.contentSecondary}
              axisLine={axisLine}
              tickSize={0}
              tickMargin={12}
              minTickGap={35}
              type="number"
              interval="preserveEnd"
              dataKey={graphData[0].graphKey}
              domain={yAxisStartOptions}
            >
              {yAxisLabelText && (
                <Label
                  offset={0}
                  position="insideLeft"
                  angle={-90}
                  dy={30}
                  style={{
                    fill: theme.styleguideColors.contentSecondary,
                    fontSize: '11px',
                  }}
                >
                  {yAxisLabelText}
                </Label>
              )}
            </YAxis>
            <RechartsTooltip
              content={
                <GraphTooltip format={tooltipFormatter || yAxisTickFormatter} />
              }
            />
            {graphDataDesc.map((item) => (
              <Area
                key={item.graphKey}
                type="linear"
                dataKey={item.graphKey}
                stroke={item.themeColor}
                fillOpacity={1}
                fill={`url(#${item.graphKey})`}
              />
            ))}
          </AreaChart>
        )}
      </ResponsiveContainer>
    </ChartContainer>
  );
};
